import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom'; // <--- 1. Importante
import { Search, MapPin, ChevronDown, Calendar, User, X, ChevronLeft, ChevronRight } from 'lucide-react';
import { getProperties } from '../services/apiService';
import { Property } from '../types';

interface SearchBarProps {
    params: { location: string; checkIn: string; checkOut: string; guests: number };
    setParams: React.Dispatch<React.SetStateAction<{ location: string; checkIn: string; checkOut: string; guests: number }>>;
    onSearch: () => void;
}

// --- HELPER DATE FUNCTIONS ---
const getDaysInMonth = (year: number, month: number) => new Date(year, month + 1, 0).getDate();
const getFirstDayOfMonth = (year: number, month: number) => new Date(year, month, 1).getDay(); // 0 = Sunday

const SearchBar = ({ params, setParams, onSearch }: SearchBarProps) => {
    const [cities, setCities] = useState<string[]>([]);
    const [loading, setLoading] = useState(true);

    // --- CALENDAR STATE ---
    const [isCalendarOpen, setIsCalendarOpen] = useState(false);
    const [calendarViewDate, setCalendarViewDate] = useState(new Date());
    const [selectingField, setSelectingField] = useState<'checkIn' | 'checkOut'>('checkIn');

    useEffect(() => {
        const fetchCities = async () => {
            try {
                const data = await getProperties();
                if (data) {
                    const properties = data as unknown as Property[];
                    const uniqueCities = Array.from(new Set(
                        properties
                            .map((p) => p.city)
                            .filter((c): c is string => !!c && c.trim() !== '')
                    )).sort();
                    setCities(uniqueCities);
                }
            } catch (error) {
                console.error("Error fetching cities", error);
            } finally {
                setLoading(false);
            }
        };
        fetchCities();
    }, []);

    const handleChange = (field: string, value: any) => {
        setParams(prev => ({ ...prev, [field]: value }));
    };

    // --- CALENDAR LOGIC ---
    const openCalendar = (field: 'checkIn' | 'checkOut') => {
        setSelectingField(field);
        setIsCalendarOpen(true);
    };

    const handleMonthChange = (increment: number) => {
        const newDate = new Date(calendarViewDate);
        newDate.setMonth(newDate.getMonth() + increment);
        setCalendarViewDate(newDate);
    };

    const handleDateSelect = (day: number) => {
        const year = calendarViewDate.getFullYear();
        const month = calendarViewDate.getMonth();
        const selectedDate = new Date(year, month, day);
        // Usa toLocaleDateString con formato corretto o costruisci manualmente per evitare problemi fuso orario
        const dateStr = [
            selectedDate.getFullYear(),
            String(selectedDate.getMonth() + 1).padStart(2, '0'),
            String(selectedDate.getDate()).padStart(2, '0')
        ].join('-');

        if (selectingField === 'checkIn') {
            handleChange('checkIn', dateStr);
            if (params.checkOut && dateStr > params.checkOut) {
                handleChange('checkOut', '');
            }
            setSelectingField('checkOut');
        } else {
            if (params.checkIn && dateStr < params.checkIn) {
                handleChange('checkIn', dateStr);
                handleChange('checkOut', '');
            } else {
                handleChange('checkOut', dateStr);
                setIsCalendarOpen(false);
            }
        }
    };

    const renderCalendarGrid = () => {
        const year = calendarViewDate.getFullYear();
        const month = calendarViewDate.getMonth();
        const daysInMonth = getDaysInMonth(year, month);
        const firstDay = getFirstDayOfMonth(year, month);

        const days = [];
        for (let i = 0; i < firstDay; i++) {
            days.push(<div key={`empty-${i}`} className="h-10 md:h-12"></div>);
        }

        const todayStr = new Date().toISOString().split('T')[0];

        for (let d = 1; d <= daysInMonth; d++) {
            const currentObj = new Date(year, month, d);
            const dateStr = [
                currentObj.getFullYear(),
                String(currentObj.getMonth() + 1).padStart(2, '0'),
                String(currentObj.getDate()).padStart(2, '0')
            ].join('-');

            const isPast = dateStr < todayStr;
            const isSelected = dateStr === params.checkIn || dateStr === params.checkOut;
            const isInRange = params.checkIn && params.checkOut && dateStr > params.checkIn && dateStr < params.checkOut;

            let bgClass = "hover:bg-gray-100 text-gray-700";
            if (isPast) {
                bgClass = "text-gray-300 cursor-not-allowed line-through decoration-gray-300";
            } else if (isSelected) {
                bgClass = "bg-brand text-white hover:bg-brand shadow-md scale-105 rounded-lg";
            } else if (isInRange) {
                bgClass = "bg-brand/10 text-brand font-bold rounded-none";
            } else {
                bgClass += " rounded-lg";
            }

            days.push(
                <button
                    key={d}
                    disabled={isPast}
                    onClick={() => !isPast && handleDateSelect(d)}
                    className={`h-10 md:h-12 w-full flex items-center justify-center text-sm md:text-base font-medium transition-all ${bgClass}`}
                >
                    {d}
                </button>
            );
        }
        return days;
    };

    return (
        <>
            {/* BARRA DI RICERCA */}
            <div className="bg-white rounded-[2rem] md:rounded-full shadow-2xl flex flex-col md:flex-row items-center gap-2 md:gap-0 p-4 md:p-2 max-w-5xl mx-auto mt-[-50px] md:mt-[-40px] relative z-20 border border-gray-100 animate-fade-in">

                {/* Location Select */}
                <div className="flex-1 w-full md:w-auto px-6 py-3 md:py-2 hover:bg-gray-50 md:rounded-full rounded-2xl transition-colors relative group cursor-pointer">
                    <label className="block text-[10px] md:text-xs font-extrabold tracking-wider uppercase text-gray-500 mb-1 ml-1">Where</label>
                    <div className="relative flex items-center">
                        <MapPin className="w-4 h-4 text-brand absolute left-0 opacity-50 group-hover:opacity-100 transition-opacity" />
                        <select
                            value={params.location}
                            onChange={(e) => handleChange('location', e.target.value)}
                            disabled={loading}
                            className="w-full bg-transparent outline-none text-gray-800 text-sm font-bold appearance-none cursor-pointer pl-6 pr-6 truncate placeholder-gray-400"
                        >
                            <option value="">Anywhere</option>
                            {cities.map(city => (
                                <option key={city} value={city}>{city}</option>
                            ))}
                        </select>
                        <ChevronDown className="w-4 h-4 text-gray-400 absolute right-0 pointer-events-none" />
                    </div>
                </div>

                <div className="hidden md:block w-px h-10 bg-gray-200 mx-2"></div>

                {/* Check-in Date */}
                <div
                    onClick={() => openCalendar('checkIn')}
                    className="flex-1 w-full md:w-auto px-6 py-3 md:py-2 hover:bg-gray-50 md:rounded-full rounded-2xl transition-colors group cursor-pointer"
                >
                    <label className="block text-[10px] md:text-xs font-extrabold tracking-wider uppercase text-gray-500 mb-1 ml-1">Check-in</label>
                    <div className="relative flex items-center">
                        <Calendar className="w-4 h-4 text-brand absolute left-0 opacity-50 group-hover:opacity-100 transition-opacity" />
                        <div className={`pl-6 text-sm font-bold ${params.checkIn ? 'text-gray-800' : 'text-gray-400 font-normal'}`}>
                            {params.checkIn || 'Add date'}
                        </div>
                    </div>
                </div>

                <div className="hidden md:block w-px h-10 bg-gray-200 mx-2"></div>

                {/* Check-out Date */}
                <div
                    onClick={() => openCalendar('checkOut')}
                    className="flex-1 w-full md:w-auto px-6 py-3 md:py-2 hover:bg-gray-50 md:rounded-full rounded-2xl transition-colors group cursor-pointer"
                >
                    <label className="block text-[10px] md:text-xs font-extrabold tracking-wider uppercase text-gray-500 mb-1 ml-1">Check-out</label>
                    <div className="relative flex items-center">
                        <Calendar className="w-4 h-4 text-brand absolute left-0 opacity-50 group-hover:opacity-100 transition-opacity" />
                        <div className={`pl-6 text-sm font-bold ${params.checkOut ? 'text-gray-800' : 'text-gray-400 font-normal'}`}>
                            {params.checkOut || 'Add date'}
                        </div>
                    </div>
                </div>

                <div className="hidden md:block w-px h-10 bg-gray-200 mx-2"></div>

                {/* Guests & Search Button */}
                <div className="flex-1 w-full md:w-auto pl-6 pr-2 py-2 flex justify-between items-center hover:bg-gray-50 md:rounded-full rounded-2xl transition-colors group">
                    <div className="flex flex-col mr-4 w-full cursor-pointer">
                        <label className="block text-[10px] md:text-xs font-extrabold tracking-wider uppercase text-gray-500 mb-1 ml-1">Guests</label>
                        <div className="relative flex items-center">
                            <User className="w-4 h-4 text-brand absolute left-0 opacity-50 group-hover:opacity-100 transition-opacity" />
                            <input
    type="number"
    min="1"
    max="20"
    // MODIFICA 1: Se guests è 0 (o null/undefined), mostra stringa vuota
    value={params.guests || ''}
    
    // MODIFICA 2: Gestione più permissiva durante la digitazione
    onChange={(e) => {
        const val = parseInt(e.target.value);
        // Se l'utente cancella tutto (NaN), impostiamo a 0 temporaneamente
        handleChange('guests', isNaN(val) ? 0 : val);
    }}
    
    // MODIFICA 3: Quando l'utente esce dal campo, se è vuoto o 0, forziamo a 1
    onBlur={() => {
        if (!params.guests || params.guests < 1) {
            handleChange('guests', 1);
        }
    }}
    
    className="w-full bg-transparent outline-none text-gray-800 text-sm font-bold pl-6"
    placeholder="Add guests"
/>
                        </div>
                    </div>

                    <button
                        onClick={onSearch}
                        className="bg-brand text-white p-4 md:px-6 md:py-4 rounded-full hover:bg-red-700 transition-all shadow-lg hover:shadow-xl transform hover:scale-105 flex items-center gap-2 flex-shrink-0"
                    >
                        <Search className="w-5 h-5 stroke-white stroke-[2.5]" />
                        <span className="hidden lg:block font-bold pr-1">Search</span>
                    </button>
                </div>
            </div>

            {/* --- MODALE CALENDARIO (RENDERIZZATO NEL PORTAL) --- */}
            {isCalendarOpen && createPortal(
                <div className="fixed inset-0 z-[100] bg-black/40 backdrop-blur-sm flex items-center justify-center p-4 animate-in fade-in duration-200">
                    <div className="bg-white rounded-[2rem] shadow-2xl p-6 md:p-8 w-full max-w-lg relative border border-gray-100">
                        {/* Header Modale */}
                        <div className="flex justify-between items-center mb-6">
                            <div>
                                <h3 className="text-2xl font-serif font-bold text-gray-900">
                                    Select {selectingField === 'checkIn' ? 'Check-in' : 'Check-out'}
                                </h3>
                                <p className="text-sm text-gray-500 mt-1">
                                    {params.checkIn || 'Start'} - {params.checkOut || 'End'}
                                </p>
                            </div>
                            <button
                                onClick={() => setIsCalendarOpen(false)}
                                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                            >
                                <X className="w-6 h-6 text-gray-500" />
                            </button>
                        </div>

                        {/* Controlli Mese */}
                        <div className="flex items-center justify-between mb-4">
                            <button onClick={() => handleMonthChange(-1)} className="p-2 hover:bg-gray-100 rounded-full">
                                <ChevronLeft className="w-5 h-5 text-gray-600" />
                            </button>
                            <span className="font-bold text-lg text-gray-800 capitalize">
                                {calendarViewDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                            </span>
                            <button onClick={() => handleMonthChange(1)} className="p-2 hover:bg-gray-100 rounded-full">
                                <ChevronRight className="w-5 h-5 text-gray-600" />
                            </button>
                        </div>

                        {/* Griglia Giorni */}
                        <div className="grid grid-cols-7 text-center mb-2">
                            {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(day => (
                                <div key={day} className="text-xs font-bold text-gray-400 uppercase tracking-wider py-1">{day}</div>
                            ))}
                        </div>
                        <div className="grid grid-cols-7 gap-1 md:gap-2">
                            {renderCalendarGrid()}
                        </div>

                        {/* Footer Modale */}
                        <div className="mt-6 flex justify-between items-center pt-4 border-t border-gray-100">
                            <button
                                onClick={() => { handleChange('checkIn', ''); handleChange('checkOut', ''); }}
                                className="text-sm font-bold text-gray-400 hover:text-brand underline"
                            >
                                Clear Dates
                            </button>
                            <button
                                onClick={() => setIsCalendarOpen(false)}
                                className="bg-gray-900 text-white px-6 py-2 rounded-xl font-bold hover:bg-black transition-colors"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>,
                document.body // <-- Renderizza direttamente nel body
            )}
        </>
    );
};

export default SearchBar;
